import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class FormValidationService {
  readonly #phoneReg = new RegExp(
    String.raw`^((00|\+)[(,),-.]*)?((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)[(,),-.]*(?:\W*\d[(,),-.]*){8,12}\d$`,
  );
  readonly #passwordRegExp = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[-!@#$%^&*()_+={};:\'"|,.<>/?]).{12,}$');
  readonly #emailRegExp = new RegExp(String.raw`[A-Za-z0-9._%-]+@[A-Za-z0-9._%-]+\.[a-z]{2,3}`);
  readonly #fractionalPositiveNumberRegExp = new RegExp(String.raw`^(0|[1-9]\d*)(\.\d+)?$`);
  readonly #emailComNetDomainRegExp = /^(?!.*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|net)).*$/;
  readonly #verificationPhoneRegExp = /^\+?\d{7,15}$/;

  passwordMatchValidator(matchTo: string): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.touched && !!control.parent && !!control.parent.value && control.value === control.parent.get(matchTo).value
        ? null
        : { isMatching: true };
    };
  }

  passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value && !this.#emailComNetDomainRegExp.test(value)) {
        return { passwordWithDomainInvalid: true };
      }

      if (value && !this.#passwordRegExp.test(value)) {
        return { passwordInvalid: true };
      }

      return null;
    };
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !this.#emailRegExp.test(value)) {
        return { emailInvalid: true };
      }

      return null;
    };
  }

  phoneValidator(restrictedCodes: readonly string[] = []): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      if (!this.#phoneReg.test(value)) {
        return { phoneInvalid: true };
      }

      const digits = this.cleanPhoneNumber(String(value));
      if (restrictedCodes.some(code => digits.startsWith(code))) {
        return { restrictedPhoneCode: true };
      }

      return null;
    };
  }

  fractionalPositiveNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !this.#fractionalPositiveNumberRegExp.test(value)) {
        return { numberInvalid: true };
      }

      return null;
    };
  }

  cardNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const validCardLength = 16;
      const cleanPan = this.#cleanStringFormSpaces(control.value);
      const isValid = cleanPan.length === validCardLength && this.#luhnCheck(control.value);
      if (!isValid) {
        return { cardInvalid: true };
      }

      return null;
    };
  }

  ibanValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const minIbanLength = 16;
      const cleanIban = this.#cleanStringFormSpaces(control.value);
      const isValid = cleanIban.length >= minIbanLength && this.#isIBAN(cleanIban);
      if (!isValid) {
        return { ibanInvalid: true };
      }

      return null;
    };
  }

  safeStringValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const dangerousSymbols = ['<', '>', "'", '"', ';'];
      if (dangerousSymbols.some(symbol => control.value.includes(symbol))) {
        return { unsafe: true };
      }

      return null;
    };
  }

  verificationPhoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !this.#verificationPhoneRegExp.test(value)) {
        return { verificationPhoneInvalid: true };
      }

      return null;
    };
  }

  /**
   * The `cleanPhoneNumber` method is used to clean a phone number string by removing all non-digit characters.
   *
   * @param {string} phone - The phone number string to be cleaned.
   * @returns {string} - Returns the cleaned phone number string with only digit characters.
   */
  cleanPhoneNumber(phone: string): string {
    return phone.replaceAll(/[^0-9]/g, '');
  }

  displayPhone(phone: string | null | undefined): string {
    const value = phone?.trim() ?? '';
    if (!value) {
      return '';
    }

    return value.startsWith('+') ? value : `+${value}`;
  }

  /**
   * The `_cleanStringFormSpaces` method is used to clean a string by removing all spaces.
   *
   * @param {string} value - The string to be cleaned from spaces.
   * @returns {string} - Returns the cleaned string from spaces.
   */
  #cleanStringFormSpaces(value: string): string {
    return value.replaceAll(' ', '');
  }

  /**
   * The `_luhnCheck` method is used to calculate card number by 'Luhn' algorithm.
   *
   * @param {string} pan - The card number string to be checked.
   * @returns {boolean} - Returns card number validation result.
   */
  #luhnCheck(pan: string): boolean {
    const arr = [...pan].reverse().map(x => Number.parseInt(x));
    const lastDigit = arr.shift();
    let sum = arr.reduce((acc, val, i) => (i % 2 === 0 ? acc + ((val *= 2) > 9 ? val - 9 : val) : acc + val), 0);
    sum += lastDigit;

    return sum % 10 === 0;
  }

  /**
   * The `_luhnCheck` method is used to validate iban by 'Modulo 97' algorithm.
   *
   * @param {string} iban - The iban string to be checked.
   * @returns {boolean} - Returns iban validation result.
   */
  #isIBAN(iban: string): boolean {
    //Move front 4 digits to the end
    const rearrange = iban.slice(4) + iban.slice(0, 4);

    const alphabetArr = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];
    const alphaMap: { [key: string]: number } = {};
    const numArr: number[] = [];
    for (const [i, letter] of alphabetArr.entries()) alphaMap[letter] = i + 10;
    for (const [i, sym] of [...rearrange].entries()) numArr[i] = (alphaMap[sym] || sym) as number;

    return this.#modulo97Check(numArr);
  }

  /**
   * The `_modulo97Check` method is used to calculate iban summary by 'Modulo 97' algorithm.
   *
   * @param {string} symbolsArr - The modified IBAN symbolsArr string to be checked.
   * @returns {boolean} - Returns iban validation result.
   */
  #modulo97Check(symbolsArr: number[]): boolean {
    const div = 97;
    let tmp = '';
    for (const symbol of symbolsArr) {
      tmp += symbol.toString();
      const r = +tmp % div;
      tmp = r.toString();
    }

    return +tmp === 1;
  }
}
